import React, { useState } from "react";
import { api } from "../../lib/api";
import { INDUSTRY_NAMES, CANDIDATE_SOURCES } from "../../lib/industries";
import { FieldIn, FieldSel, FieldArea, Modal } from "./Fields";
import { Upload } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { full_name: "", phone: "", email: "", current_location: "", total_experience: "", headline: "",
  skills: "", industry: "", source: "walk_in", recruiter_notes: "", job_id: "" };

export default function AddCandidateModal({ jobs, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    if (!f.phone && !f.email) return toast.error("Add a phone number or email");
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => { if (v !== "") fd.append(k, v); });
      if (file) fd.append("resume", file);
      const r = await api.post("/candidates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.duplicate ? "Existing candidate found — profile updated (no duplicate created)" : "Candidate added to database");
      if (f.job_id) toast[r.data.application ? "success" : "info"](r.data.application ? "Linked to job" : "Already linked to this job");
      onSaved(r.data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Modal title="Add Candidate" onClose={onClose} testid="add-candidate-modal">
      <form onSubmit={save} className="space-y-4">
        <p className="text-xs text-slate-500 -mt-2">If the phone or email already exists, we merge into the existing profile instead of creating a duplicate.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <FieldIn label="Full Name" required val={f.full_name} onChange={set("full_name")} tid="ac-name" />
          <FieldIn label="Mobile / WhatsApp" val={f.phone} onChange={set("phone")} tid="ac-phone" />
          <FieldIn label="Email" type="email" val={f.email} onChange={set("email")} tid="ac-email" />
          <FieldIn label="Current Location" val={f.current_location} onChange={set("current_location")} tid="ac-location" />
          <FieldIn label="Total Experience (yrs)" type="number" val={f.total_experience} onChange={set("total_experience")} tid="ac-exp" />
          <FieldIn label="Current Role / Headline" val={f.headline} onChange={set("headline")} tid="ac-headline" />
          <FieldIn label="Skills (comma-separated)" val={f.skills} onChange={set("skills")} tid="ac-skills" />
          <FieldSel label="Industry" val={f.industry} onChange={set("industry")} opts={INDUSTRY_NAMES} placeholder="Select industry" tid="ac-industry" />
          <FieldSel label="Source" val={f.source} onChange={set("source")} opts={CANDIDATE_SOURCES} tid="ac-source" />
          <FieldSel label="Link to Job (optional)" val={f.job_id} onChange={set("job_id")} placeholder="— None —"
            opts={jobs.map(j => ({ v: j.id, l: `${j.title} · ${j.company_name}` }))} tid="ac-job" />
        </div>
        <FieldArea label="Recruiter Notes" val={f.recruiter_notes} onChange={set("recruiter_notes")} tid="ac-notes" />
        <label className="ae-btn-outline inline-flex items-center gap-2 cursor-pointer text-sm w-full justify-center" data-testid="ac-resume-label">
          <Upload className="w-4 h-4" /> {file ? file.name : "Upload Resume (PDF / DOC)"}
          <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => setFile(e.target.files[0])} data-testid="ac-resume-input" />
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="ae-btn-outline text-sm flex-1">Cancel</button>
          <button type="submit" disabled={saving} className="ae-btn-primary text-sm flex-1" data-testid="ac-submit">{saving ? "Saving…" : "Save Candidate"}</button>
        </div>
      </form>
    </Modal>
  );
}
